import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { AddonFormInput, AddonFormValues } from '../addon-form-schema';
import { ADDON_TYPES } from '../addon-form-schema';
import { SUBSCRIPTION_PRODUCT_NAMES } from '../subscription-product-names';
import { i18n } from './AddonFormFields.i18n';
import { useStyles } from './AddonFormFields.styles';

export interface AddonFormFieldsProps {
    form: UseFormReturn<AddonFormInput, unknown, AddonFormValues>;
    /**
     * Current product code that is not in SUBSCRIPTION_PRODUCT_NAMES.
     * Prepended as an option so the select can still display it (edit flow).
     */
    fallbackProductCode?: string;
    /**
     * Current addon type that is not in ADDON_TYPES.
     * Prepended as an option so the select can still display it (edit flow).
     */
    fallbackType?: string;
}

/** The shared addon form fields used by both the create and edit modals. */
export const AddonFormFields = ({
    form,
    fallbackProductCode,
    fallbackType,
}: AddonFormFieldsProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const {
        register,
        control,
        formState: { errors },
    } = form;

    return (
        <div className={classes.fields}>
            <Controller
                name="name"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        select
                        label={intl.formatMessage(i18n.productName)}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        fullWidth
                        automation-id="field-product-name"
                    >
                        {fallbackProductCode && (
                            <MenuItem key={fallbackProductCode} value={fallbackProductCode}>
                                {fallbackProductCode}
                            </MenuItem>
                        )}
                        {Object.entries(SUBSCRIPTION_PRODUCT_NAMES).map(([code, displayName]) => (
                            <MenuItem key={code} value={code}>
                                {displayName}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            />
            <Controller
                name="type"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        select
                        label={intl.formatMessage(i18n.type)}
                        error={!!errors.type}
                        helperText={errors.type?.message}
                        fullWidth
                        automation-id="field-type"
                    >
                        {fallbackType && (
                            <MenuItem key={fallbackType} value={fallbackType}>
                                {fallbackType}
                            </MenuItem>
                        )}
                        {ADDON_TYPES.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            />
            <TextField
                {...register('quantity')}
                label={intl.formatMessage(i18n.quantity)}
                type="number"
                error={!!errors.quantity}
                helperText={errors.quantity?.message}
                fullWidth
                inputProps={{ min: 0 }}
                automation-id="field-quantity"
            />
            <TextField
                {...register('salesforceQuoteNumber')}
                label={intl.formatMessage(i18n.quoteId)}
                error={!!errors.salesforceQuoteNumber}
                helperText={errors.salesforceQuoteNumber?.message}
                fullWidth
                automation-id="field-quote-id"
            />
            <TextField
                {...register('startDate')}
                label={intl.formatMessage(i18n.startDate)}
                type="date"
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
                fullWidth
                InputLabelProps={{ shrink: true }}
                automation-id="field-start-date"
            />
            <TextField
                {...register('finishDate')}
                label={intl.formatMessage(i18n.finishDate)}
                type="date"
                error={!!errors.finishDate}
                helperText={errors.finishDate?.message}
                fullWidth
                InputLabelProps={{ shrink: true }}
                automation-id="field-finish-date"
            />
            <TextField
                {...register('notes')}
                label={intl.formatMessage(i18n.notes)}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                fullWidth
                multiline
                minRows={2}
                automation-id="field-notes"
            />
        </div>
    );
};
